<template>
  <div class="about">
    <ProgressBar
      :lengthBar="lengthBar"
      :skewBar="skewBar"
    />
    <div class="scrollbar">
      <main>
        <InfoIntro :data="dataPage.intro" />

        <InfoSteps :data="dataPage.steps" />

        <InfoHost />

        <Quote :data="dataPage.quote" />

        <InfoDescription :data="dataPage.description" />

        <InfoClosing />

        <Social class="social__wrapper--mobile" />
      </main>
    </div>
  </div>
</template>

<script>
import gsap from 'gsap';
import { mapGetters } from 'vuex';
import Scrollbar, { ScrollbarPlugin } from 'smooth-scrollbar';

import ProgressBar from '@/components/ProgressBar';
import InfoIntro from '@/components/InfoIntro';
import InfoSteps from '@/components/InfoSteps';
import InfoHost from '@/components/InfoHost';
import InfoDescription from '@/components/InfoDescription';
import InfoTeam from '@/components/InfoTeam';
import Quote from '@/components/Quote';
import InfoClosing from '@/components/InfoClosing';
import Social from '@/components/Social';

import store from '@/store';
import animations from '@/scripts/animations';

import { hasClass } from '@/scripts/utils';


export default {
  name: 'About',

  data() {
    return {
      dataPage: store.getters.getDataAbout.about[0],
      lengthBar: 0,
      skewBar: 0,
      prevDir: 'up',
      prevValDir: 0,
      lastTime: Date.now(),
      lastOffset: 0,
      reduceAmount: 0,
    };
  },

  computed: {
    ...mapGetters(['getDataAbout']),
  },

  components: {
    ProgressBar,
    InfoIntro,
    InfoSteps,
    InfoHost,
    InfoDescription,
    InfoTeam,
    Quote,
    InfoClosing,
    Social,
  },

  mounted() {
    this.menu = document.querySelector('header');
    if (hasClass(this.menu, 'hide')) this.menu.classList.remove('hide');
    if (hasClass(this.menu, 'theme-dark')) this.menu.classList.remove('theme-dark');

    this.$store.commit('ADD_BUTTONS', this.$el);

    class DisableScrollPlugin extends ScrollbarPlugin {
      static pluginName = 'disableScroll';

      static defaultOptions = {
        direction: 'x',
      };

      transformDelta(delta) {
        if (this.options.direction) {
          delta[this.options.direction] = 0;
        }
        return { ...delta };
      }
    }
    Scrollbar.use(DisableScrollPlugin);

    const scrollbar = this.$el.querySelector('.scrollbar');
    this.scrollbar = new Scrollbar(scrollbar);

    this.parallaxEls = Array.from(this.$el.querySelectorAll('[data-parallax]'));
    this.parallaxElsIn = Array.from(this.$el.querySelectorAll('[data-parallaxin]'));
    this.animEls = Array.from(this.$el.querySelectorAll('[data-anim]'));
    gsap.set(this.animEls, { autoAlpha: 0 });

    this.scrollbar.addListener(this.scrollHandler.bind(this));
  },

  methods: {
    scrollHandler() {
      this.lengthBar = this.scrollbar.offset.y / this.scrollbar.limit.y;

      this.checkMenu();
      this.setSkewBar();

      this.parallaxEls.forEach((el) => {
        if (this.scrollbar.isVisible(el)) {
          animations.get('parallax')(el);
        }
      });

      this.parallaxElsIn.forEach((el) => {
        if (this.scrollbar.isVisible(el)) {
          animations.get('parallaxIn')(el);
        }
      });

      this.animEls.forEach((animEl) => {
        const type = animEl.getAttribute('data-anim');

        if (this.scrollbar.isVisible(animEl) && !animEl.hasAttribute('data-animated')) {
          const offsetEl = animEl.getBoundingClientRect().top;

          if (offsetEl <= (window.innerHeight / 1.3)) {
            animEl.setAttribute('data-animated', '');

            animations.get(type)(animEl);
          }
        }
      });
    },

    checkMenu() {
      this.direction = this.prevValDir < this.scrollbar.offset.y ? 'down' : 'up';
      this.prevValDir = this.scrollbar.offset.y;

      if (this.scrollbar.offset.y > 100 && !hasClass(this.menu, 'theme-dark')) {
        this.menu.classList.add('theme-dark');
      } else if (this.scrollbar.offset.y < 100 && hasClass(this.menu, 'theme-dark')) {
        this.menu.classList.remove('theme-dark');
      }

      if (this.direction === 'down' && !hasClass(this.menu, 'hide') && this.scrollbar.offset.y > 0) {
        this.menu.classList.add('hide');
      } else if (this.direction === 'up' && hasClass(this.menu, 'hide')) {
        this.menu.classList.remove('hide');
      }
    },

    setSkewBar() {
      const current = Date.now();
      const offset = this.scrollbar.offset.y;
      let duration = current - this.lastTime;

      if (!duration || offset === this.lastOffset) return;

      if (duration > 100) {
        this.reduceAmount += (duration - 1);
        duration -= (duration - 1);
      }

      const velocity = (offset - this.lastOffset) / duration;
      this.lastTime = current;
      this.lastOffset = offset;

      const skew = Math.min(Math.abs(velocity) * 50, 60);
      this.skewBar = (this.direction === 'up' ? skew : skew * -1);
    },
  },

  beforeDestroy() {
    this.scrollbar.removeListener(this.scrollHandler.bind(this));

    setTimeout(() => {
      if (hasClass(this.menu, 'hide')) this.menu.classList.remove('hide');
      if (hasClass(this.menu, 'theme-dark')) this.menu.classList.remove('theme-dark');

      this.scrollbar.destroy();
    }, 1400);
  },

  beforeRouteEnter(from, to, next) {
    if (store.getters.getDataAbout.length < 1) {
      store.dispatch('FETCH_ABOUT').then(() => {
        console.log('DATAABOUT FETCHED 😃');
        next();
      });
    } else {
      next(() => { console.log('DATAABOUT RETRIEVE FROM STORE 🤘'); });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/colors';
@import '../application';

.about {
  position: relative;
  display: inline-block;

  .scrollbar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  main {
    position: relative;
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    padding: 0;

    z-index: 1;
  }

  .social__wrapper--mobile {
    display: flex;
    padding: 60px 40px;
    margin: 0;
    text-align: left;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;

    @include mq(sm) {
      display: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/About.vue